import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSocket } from '../context/SocketContext';

const SupervisorDashboard = ({ userId, roomId }) => {
  const [connectionStates, setConnectionStates] = useState(new Map());

  const { socket, isConnected, roomState, alerts, muteStudent, clearAlerts } = useSocket();
  const [remoteStreams, setRemoteStreams] = useState(new Map());
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showAlerts, setShowAlerts] = useState(true);
  const peerConnections = useRef(new Map());
  const pendingCandidates = useRef(new Map());

  useEffect(() => {
    if (!socket) return;

    const handleIceCandidate = async ({ candidate, senderSocketId }) => {
      const peerConnection = peerConnections.current.get(senderSocketId);
      
      if (!peerConnection) {
        console.log('No peer connection found for:', senderSocketId);
        return;
      }

      try {
        if (peerConnection.remoteDescription && peerConnection.remoteDescription.type) {
          await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
          console.log('Added ICE candidate immediately');
        } else {
          // Store candidate for later
          if (!pendingCandidates.current.has(senderSocketId)) {
            pendingCandidates.current.set(senderSocketId, []);
          }
          pendingCandidates.current.get(senderSocketId).push(candidate);
          console.log('Stored ICE candidate for later processing');
        }
      } catch (err) {
        console.error('Error handling ICE candidate:', err);
      }
    };

    socket.on('ice-candidate', handleIceCandidate);
    return () => socket.off('ice-candidate', handleIceCandidate);
  }, [socket]);

  // Create peer connection for student
  const createPeerConnection = useCallback((studentSocketId) => {
    console.log('Creating new peer connection for student:', studentSocketId);
    
    const configuration = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' }
      ]
    };

    if (peerConnections.current.has(studentSocketId)) {
      const oldConnection = peerConnections.current.get(studentSocketId);
      oldConnection.close();
      peerConnections.current.delete(studentSocketId);
    }

    const peerConnection = new RTCPeerConnection(configuration);

    // Enable unified plan for better stream handling
    peerConnection.addTransceiver('video', {direction: 'recvonly'});
    peerConnection.addTransceiver('audio', {direction: 'recvonly'});

    // ICE candidate handling
    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('Sending ICE candidate to:', studentSocketId);
        socket.emit('ice-candidate', {
          targetSocketId: studentSocketId,
          candidate: event.candidate
        });
      }
    };

    // Connection state monitoring
    peerConnection.onconnectionstatechange = () => {
      const state = peerConnection.connectionState;
      console.log(`Connection state changed to: ${state} for student: ${studentSocketId}`);
      setConnectionStates(prev => new Map(prev).set(studentSocketId, state));
      
      // Clean up if connection failed or closed
      if (state === 'failed' || state === 'closed') {
        console.log('Connection failed/closed, cleaning up:', studentSocketId);
        setRemoteStreams(prev => {
          const newMap = new Map(prev);
          newMap.delete(studentSocketId);
          return newMap;
        });
      }
    };

    // Handle incoming tracks
    peerConnection.ontrack = (event) => {
      console.log('Received track:', event.track.kind, 'from:', studentSocketId);
      
      if (!event.streams || !event.streams[0]) {
        console.warn('No stream received with track');
        return;
      }

      const stream = event.streams[0];
      console.log('Stream received:', stream.id, 'active:', stream.active);

      setRemoteStreams(prev => {
        const newStreams = new Map(prev);
        newStreams.set(studentSocketId, stream);
        return newStreams;
      });
    };

    // Create and send offer immediately
    peerConnection.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true
    })
    .then(offer => {
      console.log('Created offer for:', studentSocketId);
      return peerConnection.setLocalDescription(offer);
    })
    .then(() => {
      console.log('Set local description, sending offer');
      socket.emit('offer', {
        targetSocketId: studentSocketId,
        offer: peerConnection.localDescription
      });
    })
    .catch(err => console.error('Error creating/sending offer:', err));

    peerConnections.current.set(studentSocketId, peerConnection);
    return peerConnection;
  }, [socket]);

  useEffect(() => {
    const interval = setInterval(() => {
      console.log('Current remote streams:', remoteStreams.size);
      console.log('Current peer connections:', peerConnections.current.size);
      remoteStreams.forEach((stream, socketId) => {
        console.log('Stream for', socketId, {
          id: stream.id,
          active: stream.active,
          tracks: stream.getTracks().map(t => ({
            kind: t.kind,
            enabled: t.enabled,
            readyState: t.readyState
          }))
        });
      });
    }, 10000); // Increase interval to 10 seconds

    return () => clearInterval(interval);
  }, [remoteStreams]);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    const handleAnswer = async ({ answer, senderSocketId }) => {
      console.log('Received answer from:', senderSocketId);
      
      const peerConnection = peerConnections.current.get(senderSocketId);
      if (!peerConnection) {
        console.log('No peer connection found for answer from:', senderSocketId);
        return;
      }

      try {
        await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
        console.log('Set remote description successfully');
        
        // Now process any pending candidates
        const pendingCandidatesList = pendingCandidates.current.get(senderSocketId) || [];
        console.log(`Processing ${pendingCandidatesList.length} pending candidates`);
        
        for (const candidate of pendingCandidatesList) {
          try {
            await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
            console.log('Added stored ICE candidate');
          } catch (err) {
            console.error('Error adding stored ICE candidate:', err);
          }
        }
        
        // Clear pending candidates
        pendingCandidates.current.delete(senderSocketId);
        
      } catch (err) {
        console.error('Error handling answer:', err);
      }
    };

    socket.on('answer', handleAnswer);
    
    return () => {
      socket.off('answer', handleAnswer);
    };
  }, [socket]);

  // Handle room state changes
  useEffect(() => {
    if (!roomState?.students) return;

    // Create peer connections for new students
    roomState.students.forEach(student => {
      if (!peerConnections.current.has(student.socketId)) {
        console.log('Creating peer connection for new student:', student.userId);
        createPeerConnection(student.socketId);
      }
    });

    // Clean up peer connections for students who left
    const currentStudentSockets = new Set(roomState.students.map(s => s.socketId));
    Array.from(peerConnections.current.keys()).forEach(socketId => {
      if (!currentStudentSockets.has(socketId)) {
        console.log('Cleaning up peer connection for:', socketId);
        const pc = peerConnections.current.get(socketId);
        if (pc) {
          pc.close();
          peerConnections.current.delete(socketId);
        }
        setRemoteStreams(prev => {
          const newMap = new Map(prev);
          newMap.delete(socketId);
          return newMap;
        });
        setConnectionStates(prev => {
          const newMap = new Map(prev);
          newMap.delete(socketId);
          return newMap;
        });
      }
    });
  }, [roomState.students, createPeerConnection]);

  // Handle mute/unmute
  const handleMuteToggle = (studentUserId, currentMuteStatus) => {
    muteStudent(studentUserId, !currentMuteStatus);
  };

  // Calculate grid layout
  const getGridLayout = (count) => {
    if (count <= 1) return 'grid-cols-1';
    if (count <= 4) return 'grid-cols-2';
    if (count <= 9) return 'grid-cols-3';
    return 'grid-cols-4';
  };

  // Get recent alerts for student
  const getStudentAlerts = (studentUserId) => {
    return alerts.filter(alert => 
      alert.userId === studentUserId && 
      Date.now() - alert.timestamp < 30000 // Last 30 seconds
    );
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('Cleaning up all peer connections');
      peerConnections.current.forEach((pc, socketId) => {
        console.log('Closing peer connection:', socketId);
        pc.close();
      });
      peerConnections.current.clear();
      pendingCandidates.current.clear();
    };
  }, []);

  console.log("remoteStreams:", remoteStreams);
  console.log("connectionStates:", connectionStates);

  return (
    <div className="h-full bg-gray-900 flex flex-col">
      {/* Control Panel */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-sm text-gray-300">
                {isConnected ? 'Terhubung' : 'Terputus'}
              </span>
            </div>
            <div className="text-sm text-gray-300">
              <span className="font-semibold">{roomState?.students?.length || 0}</span> Peserta
            </div>
            <div className="text-sm text-gray-300">
              <span className="font-semibold">{alerts?.length || 0}</span> Peringatan
            </div>
            <div className="text-sm text-gray-300">
              <span className="font-semibold">{remoteStreams.size}</span> Video Streams
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowAlerts(!showAlerts)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                showAlerts 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {showAlerts ? 'Sembunyikan' : 'Tampilkan'} Alert
            </button>
            <button
              onClick={clearAlerts}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
            >
              Bersihkan Alert ({alerts?.length || 0})
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Main Video Grid */}
        <div className="flex-1 p-4">
          {!roomState?.students || roomState.students.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-gray-400">
                <svg className="h-16 w-16 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="text-lg font-medium mb-2">Tidak Ada Peserta</h3>
                <p className="text-sm">Menunggu peserta bergabung ke ruangan...</p>
              </div>
            </div>
          ) : (
            <div className={`grid ${getGridLayout(roomState.students.length)} gap-4 h-full`}>
              {roomState.students.map((student) => {
                const studentAlerts = getStudentAlerts(student.userId);
                const hasRecentAlert = studentAlerts.length > 0;
                const latestAlert = studentAlerts[0];
                const connectionState = connectionStates.get(student.socketId);
                const hasStream = remoteStreams.has(student.socketId);
                
                return (
                  <div
                    key={student.socketId}
                    className={`relative bg-gray-800 rounded-lg overflow-hidden border-2 transition-all ${
                      hasRecentAlert 
                        ? 'border-red-500 shadow-lg shadow-red-500/20' 
                        : selectedStudent === student.userId
                        ? 'border-blue-500'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                    onClick={() => setSelectedStudent(student.userId)}
                  >
                    {/* Video Stream */}
                    <div className="aspect-video bg-gray-900 relative">
                      {hasStream ? (
                        <div className="w-full h-full">
                          <VideoStream 
                            key={`${student.socketId}-${remoteStreams.get(student.socketId)?.id}`}
                            student={student}
                            stream={remoteStreams.get(student.socketId)}
                          />
                          <div className="absolute bottom-2 right-2 bg-black/50 px-2 py-1 rounded text-xs text-white">
                            {remoteStreams.get(student.socketId)?.getTracks().length || 0} tracks
                          </div>
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="text-center text-gray-400">
                            <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-2">
                              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <span className="text-xs">
                              {connectionState === 'connecting' 
                                ? 'Menghubungkan...'
                                : connectionState === 'connected'
                                ? 'Menunggu Video...'
                                : 'Tidak Terhubung'}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Connection Status */}
                      <div className="absolute bottom-2 left-2 flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${
                          connectionState === 'connected' 
                            ? 'bg-green-500' 
                            : connectionState === 'connecting'
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        }`} />
                        <span className="text-xs text-gray-300">
                          {connectionState || 'new'}
                        </span>
                      </div>
                      
                      {/* Alert Badge */}
                      {hasRecentAlert && (
                        <div className="absolute top-2 right-2">
                          <div className="bg-red-500 text-white text-xs px-2 py-1 rounded-full animate-pulse">
                            Alert!
                          </div>
                        </div>
                      )}

                      {/* Active Status */}
                      <div className="absolute top-2 left-2">
                        <div className={`w-2 h-2 rounded-full ${
                          student.isActive ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                      </div>
                    </div>

                    {/* Student Info & Controls */}
                    <div className="p-3 bg-gray-800">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-white truncate">
                            {student.userId}
                          </h3>
                          {hasRecentAlert && latestAlert && (
                            <p className="text-xs text-red-400 truncate">
                              {latestAlert.message}
                            </p>
                          )}
                        </div>
                        
                        {/* Audio Control */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMuteToggle(student.userId, student.audioMuted);
                          }}
                          className={`ml-2 p-1.5 rounded-full transition-colors ${
                            student.audioMuted
                              ? 'bg-red-600 hover:bg-red-700 text-white'
                              : 'bg-green-600 hover:bg-green-700 text-white'
                          }`}
                          title={student.audioMuted ? 'Unmute' : 'Mute'}
                        >
                          {student.audioMuted ? (
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clipRule="evenodd" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                            </svg>
                          ) : (
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 14.142M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Alert Panel */}
        {showAlerts && (
          <div className="w-80 bg-gray-800 border-l border-gray-700 flex flex-col">
            <div className="p-4 border-b border-gray-700">
              <h2 className="text-lg font-semibold text-white">Peringatan Real-time</h2>
              <p className="text-sm text-gray-400">
                {alerts?.length || 0} peringatan total
              </p>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {!alerts || alerts.length === 0 ? (
                <div className="p-4 text-center text-gray-400">
                  <svg className="h-12 w-12 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm">Tidak ada peringatan</p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {alerts.map((alert) => {
                    const timeAgo = Math.floor((Date.now() - alert.timestamp) / 1000);
                    const isRecent = timeAgo < 10;
                    
                    return (
                      <div
                        key={alert.id}
                        className={`p-3 rounded-lg border transition-all ${
                          isRecent
                            ? 'bg-red-900/30 border-red-500/50 shadow-lg'
                            : 'bg-gray-700/50 border-gray-600/50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-white">
                                {alert.userId}
                              </span>
                              {isRecent && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Baru
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-300 mt-1">
                              {alert.message}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {timeAgo < 60 
                                ? `${timeAgo} detik yang lalu`
                                : `${Math.floor(timeAgo / 60)} menit yang lalu`
                              }
                            </p>
                          </div>
                          
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${
                            alert.type === 'face-detection' ? 'bg-yellow-500' : 'bg-red-500'
                          }`}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const VideoStream = React.memo(({ student, stream }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      console.log('Setting up video stream for:', student.userId, 'stream active:', stream.active);
      videoRef.current.srcObject = stream;
    }
    
    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [stream, student.userId]);

  const handleLoadedMetadata = () => {
    console.log('Video metadata loaded for:', student.userId);
    if (videoRef.current) {
      videoRef.current.play()
        .then(() => console.log('Video playing for:', student.userId))
        .catch(err => console.error('Error playing video for', student.userId, ':', err));
    }
  };

  const handleError = (e) => {
    console.error('Video error for', student.userId, ':', e);
  };

  const handleCanPlay = () => {
    console.log('Video can play for:', student.userId);
  };

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted
      className="w-full h-full object-cover"
      onLoadedMetadata={handleLoadedMetadata}
      onError={handleError}
      onCanPlay={handleCanPlay}
    />
  );
});

export default SupervisorDashboard;